import React from 'react';
import { useRouter } from 'next/router';
import { Container, Stack, Heading } from '@global-ecom/nitro-uds/elements';

import { useSiteConfig } from 'hooks/useSiteConfig';
import { useTranslate } from 'hooks/useTranslations';
import { useAuth } from 'hooks/useAuth';
import { useFeature } from 'hooks/useFeature';
import { Page } from 'ui/components/Page';
import {
  AuthAction,
  AuthenticateForm,
  AuthenticateTabsProps,
} from 'ui/forms/AuthenticateForm';
import { PageHead } from 'ui/components/PageHead';
import { LayoutAccountPage } from 'ui/pages/LayoutAccountPage';
import { OTPAuthenticateForm, OTPAuthStage } from 'ui/forms/OTPForm';

const Login = () => {
  const t = useTranslate();
  const { guest } = useAuth();
  const router = useRouter();
  const { localizeUrlPath } = useSiteConfig();
  const authAction = router.query.action as AuthAction;

  React.useEffect(() => {
    if (!guest) router.replace(localizeUrlPath('/account'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!guest) return null;

  const title =
    t('pageTitleFragmentLogin') ??
    `${t('account')} ${
      authAction === AuthAction.register ? t('register') : t('login')
    }`;

  return (
    <>
      <PageHead
        title={t('pageTitle', { title })}
        description={t('pageDescriptionLogin') ?? ''}
      />
      <Page>
        <LayoutAccountPage title={t('myAccount')}>
          <CreateAccountForm />
        </LayoutAccountPage>
      </Page>
    </>
  );
};

const CreateAccountForm = () => {
  const t = useTranslate();
  const router = useRouter();
  const isOTPAuthEnabled = useFeature('OTP_LOGIN');
  const {
    localizeUrlPath,
    forms: { quickRegistration: quickRegistrationForm },
  } = useSiteConfig();

  const showQuickRegistration = Boolean(
    router.asPath.includes('showQuickRegister') && quickRegistrationForm?.length
  );

  const authAction = router.query.action;

  const handleSignInSuccess = () => {
    // Redirect the user to back where they came from or to account
    const { from, action, language, country, ...rest } = router.query;

    window.sessionStorage.setItem(
      'new-user',
      (authAction === AuthAction.register).toString()
    );

    router.push({
      pathname: localizeUrlPath((from as string) || 'account'),
      query: showQuickRegistration ? '' : rest,
    });
  };

  const authenticateFormProps: Pick<
    AuthenticateTabsProps,
    'from' | 'onSuccess' | 'resetOnOverlay' | 'showQuickRegistration'
  > = {
    from: router.query.from,
    onSuccess: handleSignInSuccess,
    resetOnOverlay: true,
    showQuickRegistration,
  };

  return (
    <Container size={showQuickRegistration ? 'xs' : 'sm'}>
      <Stack>
        {showQuickRegistration && (
          <Heading asChild>
            <h2>{t('quickAccountRegistrationHeadline')}</h2>
          </Heading>
        )}
        {isOTPAuthEnabled ? (
          <OTPAuthenticateForm
            action={authAction as OTPAuthStage}
            {...authenticateFormProps}
          />
        ) : (
          <AuthenticateForm
            action={authAction as AuthAction}
            {...authenticateFormProps}
          />
        )}
      </Stack>
    </Container>
  );
};

export default Login;
